import { HttpInterceptorFn, HttpErrorResponse, HttpEvent, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, throwError, switchMap, of, from, map } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { DiagnosticsService } from '../services/diagnostics.service';
import { PERMISSION_DENIED_CODE, extractApiErrorCode } from '../services/notification.service';
import { scrubUrl } from '../observability/sentry-init';
import { environment } from '../../../environments/environment';

/** Так бэк отвечает на протухший access-токен — на любой защищённой ручке, включая /auth/me. */
const ACCESS_TOKEN_EXPIRED = 'User not authorized. Access token expired';

/**
 * Пришёл ли 401 именно из-за протухшего access-токена. У запросов с `responseType: 'blob'`
 * (скачивание PDF) тело ошибки — Blob, и `detail` в нём надо сначала прочитать.
 */
function isAccessTokenExpired$(error: unknown): Observable<boolean> {
  if (!(error instanceof HttpErrorResponse) || error.status !== 401) return of(false);
  if (error.error instanceof Blob) {
    return from(error.error.text()).pipe(
      map((text) => JSON.parse(text)?.detail === ACCESS_TOKEN_EXPIRED),
      catchError(() => of(false)),
    );
  }
  return of(error.error?.detail === ACCESS_TOKEN_EXPIRED);
}

/** BE-1517: запрос к нашему API — относительный адрес или адрес API, а не DaData и другие внешние хосты. */
function isOwnApiRequest(url: string): boolean {
  return !/^(https?:)?\/\//i.test(url) || url.startsWith(`${environment.apiBaseUrl}/`);
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const diagnostics = inject(DiagnosticsService);

  const isAuthRequest = req.url.includes('/auth');
  // BE-1517: DaData ходит со своим ключом в заголовке: наш токен ей не подставляем, а её 401 к нашей
  // сессии отношения не имеет — ошибку получает вызывающий, и только.
  if (!isOwnApiRequest(req.url)) {
    return next(req);
  }

  // 401 ЭТИХ auth-эндпоинтов не продлевает сессию и не уходит в генерик-выход ниже:
  //  • /auth/refresh — тимдаун принадлежит refreshToken() (tap.error → единый
  //    владелец), а продление на нём — бесконечная петля refresh → 401 → refresh;
  //  • /auth/logout — иначе 401 самого логаута рекурсивно дёргает выход снова;
  //  • /auth/login — 401 здесь значит «неверный пароль», а не конец сессии: выход
  //    почистил бы общее хранилище и выбил залогиненную соседнюю вкладку (BE-1478).
  // ВСЕ прочие /auth/* (включая ЗАЩИЩЁННЫЕ /auth/me, /auth/users) — как обычные
  // запросы: протух access — продлеваем (BE-1478), прочий 401 — выход.
  const ownsTeardownOn401 =
    req.url.includes('/auth/refresh') ||
    req.url.includes('/auth/logout') ||
    req.url.includes('/auth/login');

  // BE-1518: пара в хранилище может оказаться чужой — в соседней вкладке вошли под другим
  // аккаунтом. С ней запрос не уходит; сверка заодно оживляет вкладку, если вошёл её владелец.
  // Обновление пары, выход и вход решают, чью пару слать, сами.
  const blocked = ownsTeardownOn401 ? null : authService.checkStoredPair();
  if (blocked) {
    return throwError(() => blocked);
  }

  // BE-1478: токен берём из общего хранилища в момент запроса, а не из сигнала вкладки:
  // обновила пару соседняя вкладка — эта сразу шлёт свежий. Для /auth/* заголовок
  // ставит сам ApiService (из того же хранилища).
  const accessToken = localStorage.getItem('access_token');
  if (accessToken && !isAuthRequest) {
    req = req.clone({
      setHeaders: {
        Authorization: `${localStorage.getItem('token_type') || 'bearer'} ${accessToken}`
      }
    });
  }
  // С каким токеном запрос ушёл: по нему refreshToken() поймёт, что пару уже обновила
  // другая вкладка, и не потратит ротированный refresh.
  const sentAccessToken = req.headers.get('Authorization')?.split(' ')[1] ?? null;

  /** BE-1517: `mayRetryStale` — запрос ещё не повторяли после устаревшего 401. */
  const send = (
    req: HttpRequest<unknown>,
    sentAccessToken: string | null,
    mayRetryStale: boolean,
  ): Observable<HttpEvent<unknown>> => next(req).pipe(
    catchError((error: HttpErrorResponse) => isAccessTokenExpired$(error).pipe(switchMap((accessTokenExpired) => {
      if (accessTokenExpired && !ownsTeardownOn401) {

        diagnostics.addBreadcrumb('auth', 'access token expired; refreshing', {
          url: scrubUrl(req.urlWithParams || req.url),
        }, 'warning');

        // Пытаемся обновить токен
        return authService.refreshToken(sentAccessToken).pipe(
          switchMap((newTokens) => {
            diagnostics.addBreadcrumb('auth', 'token refresh succeeded', {
              role: newTokens.role,
              business_status: newTokens.business_status,
              registration_stage: newTokens.registration_stage,
            });
            
            // Создаем новый запрос с обновленным токеном
            const newReq = req.clone({
              setHeaders: {
                Authorization: `${newTokens.token_type} ${newTokens.access_token}`
              }
            });
            
            // Повторяем оригинальный запрос с новым токеном
            return next(newReq);
          }),
          catchError((refreshError) => {
            diagnostics.addBreadcrumb('auth', 'token refresh failed', {
              url: scrubUrl(req.urlWithParams || req.url),
            }, 'error');
            // Выход (если сессии действительно нет) делает ОБЩИЙ пайп refreshToken() РОВНО
            // один раз для всей пачки параллельных 401 (см. AuthService.isHandlingAuthFailure).
            // Здесь — только пробрасываем ошибку конкретному ждущему запросу.
            return throwError(() => refreshError);
          })
        );
      }

      // Прочие 401 (НЕ истечение access-токена): выход — для ВСЕХ запросов, КРОМЕ
      // refresh/logout (см. ownsTeardownOn401), которые владеют своей 401 сами.
      // Защищённые /auth/me, /auth/users и т.п. СЮДА входят (иначе отозванная сессия
      // залипает локально). Выход локальный, без POST /auth/logout — см. expireSession().
      if (error.status === 401 && !ownsTeardownOn401) {
        // BE-1517: на вход уводит не каждый такой 401 — устаревший кончается повтором или перезагрузкой.
        diagnostics.addBreadcrumb('auth', 'unauthorized (not expired)', {
          url: scrubUrl(req.urlWithParams || req.url),
        }, 'warning');
        // BE-1517: 401 устарел, а в хранилище пара того же пользователя — повторяем запрос с ней.
        // Повтор один: его 401 разбирается заново, но уже без повтора.
        if (authService.expireSessionUnlessStale(sentAccessToken) && mayRetryStale) {
          diagnostics.addBreadcrumb('auth', 'stale 401; retrying with the stored pair', {
            url: scrubUrl(req.urlWithParams || req.url),
          });
          const storedAccess = localStorage.getItem('access_token');
          return send(
            req.clone({
              setHeaders: { Authorization: `${localStorage.getItem('token_type') || 'bearer'} ${storedAccess}` },
            }),
            storedAccess,
            false,
          );
        }
      }

      // BE-804: право сняли, а интерфейс об этом не знает — кнопка была активна, но
      // бэкенд отказал. Перечитываем профиль, чтобы гейтинг догнал реальность.
      // Разлогинивать нельзя: сессия жива, недоступно только это действие.
      if (error.status === 403 && extractApiErrorCode(error) === PERMISSION_DENIED_CODE) {
        diagnostics.addBreadcrumb('auth', 'permission denied; refreshing permissions', {
          url: scrubUrl(req.urlWithParams || req.url),
        }, 'warning');
        authService.refreshPermissions();
      }

      return throwError(() => error);
    })))
  );

  return send(req, sentAccessToken, true);
};
